import { Component, OnDestroy, OnInit } from '@angular/core';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import * as moment from 'moment';
import { AppMessagingService } from '../../../core/services/app-messaging.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { CustomerCharter } from '../../portal/charters/charter-payment/charter-payment.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { PaymentDAO } from '../../../core/services/dao/paymentDAO';
import { isBeforeCharterMoment } from '../customer-charter-details/customer-charter-details.component';
import { ChickenTestDialogService } from '../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { DialogsService } from '../../../core/services/dialog.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';

@Component({
  selector: 'app-customer-my-charters',
  templateUrl: './customer-my-charters.component.html',
  styleUrls: ['./customer-my-charters.component.scss'],
})
export class CustomerMyChartersComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  // data
  charters: CustomerCharter[] = [];
  offerCharters: CustomerCharter[] = [];
  pendingCharters: CustomerCharter[] = [];
  approvedCharters: CustomerCharter[] = [];
  upcomingCharters: CustomerCharter[] = [];
  pastCharters: CustomerCharter[] = [];
  cancelledCharters: CustomerCharter[] = [];
  // utility
  loading = true;
  subs: Subscription[] = [];
  activeTab: number;
  isMobile: boolean;
  tabStates = [
    'Offers',
    'Pending',
    'Approved',
    'Upcoming',
    'Past',
    'Cancelled',
  ];

  constructor(
    private charterDAO: CharterDAO,
    private appMessagingService: AppMessagingService,
    private router: Router,
    private route: ActivatedRoute,
    private paymentDAO: PaymentDAO,
    private chickenTestDialog: ChickenTestDialogService,
    private dialogService: DialogsService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
  }

  ngOnInit() {
    this.activeTab = 0;
    if (
      this.route.snapshot.queryParams &&
      this.route.snapshot.queryParams.activeTab
    ) {
      if (this.tabStates.includes(this.route.snapshot.queryParams.activeTab)) {
        this.activeTab = this.tabStates.findIndex((state) => {
          return state === this.route.snapshot.queryParams.activeTab;
        });
      }
    }

    const charterSub = this.charterDAO
      .getCustomerCharters()
      .subscribe((data) => {
        this.charters = data;
        this.sortCharters();
        this.loading = false;
      });
    this.subs.push(charterSub);
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  // data

  cancelCharter(charterId: number, charterType: string) {
    const updateSub = this.charterDAO
      .customerCancelCharter(charterId)
      .subscribe((res) => {
        if (res) {
          this.appMessagingService.openSnack('Charter Successfully Cancelled.');
          if (charterType === 'pending') {
            const indexToCancel = this.pendingCharters.findIndex((charter) => {
              return charter.id === charterId;
            });
            const removedCharter = this.pendingCharters.splice(
              indexToCancel,
              1,
            )[0];
            this.cancelledCharters.push(removedCharter);
          } else if (charterType === 'approved') {
            const indexToCancel = this.approvedCharters.findIndex((charter) => {
              return charter.id === charterId;
            });
            const removedCharter = this.approvedCharters.splice(
              indexToCancel,
              1,
            )[0];
            this.cancelledCharters.push(removedCharter);
          } else if (charterType === 'offer') {
            const indexToCancel = this.offerCharters.findIndex((charter) => {
              return charter.id === charterId;
            });
            const removedCharter = this.offerCharters.splice(
              indexToCancel,
              1,
            )[0];
            this.cancelledCharters.push(removedCharter);
          }
        }
      });
    this.subs.push(updateSub);
  }

  private sortCharters(): void {
    this.charters?.forEach((charter) => {
      if (!charter.cancelled) {
        if (!charter.completed) {
          const hasAvailableOffers =
            charter.offers?.length &&
            charter.offers.some((offer) => !offer.status);
          const cancelledCharter = charter.cancelled;
          if (hasAvailableOffers && !cancelledCharter) {
            this.offerCharters.push(charter);
          } else if (charter.availabilityStatus !== 'Available') {
            this.pendingCharters.push(charter);
          } else if (!charter.paymentReceived) {
            this.approvedCharters.push(charter);
          } else if (
            charter.paymentReceived &&
            isBeforeCharterMoment(charter)
          ) {
            this.upcomingCharters.push(charter);
          } else if (
            charter.paymentReceived &&
            !isBeforeCharterMoment(charter)
          ) {
            this.pastCharters.push(charter);
          }
        } else {
          this.pastCharters.push(charter);
        }
      } else {
        this.cancelledCharters.push(charter);
      }
    });
    if (
      !this.offerCharters.length &&
      this.activeTab === 0 &&
      !this.route.snapshot.queryParams.activeTab
    ) {
      this.activeTab = 1;
    }
    this.approvedCharters.forEach((charter) => {
      const paymentsSub = this.paymentDAO
        .getPaymentsByCharterId(charter.id)
        .subscribe((payments) => {
          charter.primaryPayment = payments.find((payment) => {
            return payment.type !== PaymentType.REMAINING;
          });
        });
      this.subs.push(paymentsSub);
    });
  }

  // utility

  navigateToBuyflow() {
    this.router.navigate(['/home']);
  }

  navigateToGenericPath(path: string) {
    this.router.navigate([path]);
  }

  tabChanged(event: MatTabChangeEvent) {
    const url = window.location.href;
    const urlSplit = url.split('?');
    this.activeTab = event.index;
    const obj = {
      Title: 'New title',
      Url: urlSplit[0] + '?activeTab=' + this.tabStates[event.index],
    };
    history.pushState(obj, obj.Title, obj.Url);
  }

  viewOriginalCharter(charter) {
    this.dialogService.CustomerCharterDetailsDialog(charter, this.isMobile);
  }

  formatDate(date) {
    return moment(date).format('MMM D');
  }
}
